import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { format, isValid, parseISO } from 'date-fns'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import ScrollingTable from '../Tables/ScrollingTable'
import { USER_EXPERIENCES } from '../../utils/constants'
import GridItem from './GridItem'
import LighthouseIcon from '../../assets/images/icons/lighthouse.svg'

const Container = styled('div')(({ theme }) => ({ width: '100%' }))

const DateTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#092A48',
  width: 'fit-content',
  textAlign: 'center',
  marginLeft: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px !important',
    fontWeight: 400,
  },
}))

const DownloadGridItem = styled(GridItem)(({ theme }) => ({
  display: 'flex',
  width: 'fit-content',
  cursor: 'pointer',
}))

const DownloadContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  minWidth: '100%',
  alignItems: 'center',
}))

const StyledLighthouse = styled('img')(({ theme }) => ({
  height: '20px',
  width: '20px',
  marginRight: theme.spacing(0.5),
}))

const hasDocumentData = (document) => {
  const normalized = document?.asset || document
  const { description, localAsset, uid } = normalized ?? {}
  return description && localAsset && uid
}

const FormsResources = ({ data: resources }) => {
  const data = useMemo(
    () => ({
      ...resources,
      index_methodology: {
        ...resources.index_methodology,
        as_of_date: resources.index_methodology_as_of_date,
      },
    }),
    [resources]
  )

  const fundDocuments = useMemo(() =>
    [
      { ...data.seismic_quarterly_report?.[0]?.document, as_of_date: data.seismic_quarterly_report?.[0]?.date },
      { ...data.seismic_portfolio_holdings?.[0]?.document, as_of_date: data.seismic_portfolio_holdings?.[0]?.date },
      { ...data.fact_sheet_seismic?.[0]?.document, as_of_date: data.fact_sheet_seismic?.[0]?.date }
    ].filter(hasDocumentData), [data])

  const regulatory = useMemo(
    () =>
      [
        data.summary_prospectus,
        data.prospectus,
        data.tailored_shareholder_report,
        data.semi_annual_tailored_shareholder_report,
        data.supplementary_financial_information,
        data.annual_report,
        data.semi_annual_report,
        data.sai,
        data.fiscal_quarter_end_q3,
        data.fiscal_quarter_end_q1,
        data.form_n_csr,
        {
          ...data.index_methodology,
          title:
            data.index_methodology?.description ||
            data.index_methodology?.title,
        },
        {
          ...data.index_fact_sheet,
          title:
            data.index_fact_sheet?.description || data.index_fact_sheet?.title,
        },
      ].filter(hasDocumentData),
    [data]
  )

  const formattedSeismicAdditionalDocuments = useMemo(
    () =>
      data?.seismic_additional_documents
        ?.map(({ seismic_document: docs, user_experience }) => {
          const [doc] = docs
          if (!doc) return null
          const { date, title, description, document, uid } = doc
          return {
            user_experience,
            as_of_date: date,
            asset: {
              ...document,
              title,
              description: description ?? title,
              uid,
            },
          }
        })
        .filter(Boolean) ?? [],
    [data]
  )

  const additionalDocuments = useMemo(
    () =>
      [
        ...formattedSeismicAdditionalDocuments,
        ...data.additional_documents,
      ].filter(hasDocumentData),
    [formattedSeismicAdditionalDocuments, data]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        id: 'name',
        accessor: 'description',
        Cell: ({ row }) => {
          const userExperience = row.user_experience?.experience
          const normalized = row.asset || row
          const { localAsset, description, title, uid } = normalized ?? {}
          const asOfDate = row?.as_of_date || ''
          const formattedDate = isValid(parseISO(asOfDate))
            ? format(parseISO(asOfDate), 'MM/dd/yyyy')
            : ''
          const isInstitutional =
            userExperience === USER_EXPERIENCES.INSTITUTIONAL_ONLY
          let assetUrl = localAsset?.publicURL
          return (
            <DownloadContainer>
              <DownloadGridItem
                checkUserExperience
                description={description || title}
                key={uid}
                publicURL={isInstitutional ? undefined : assetUrl}
                userExperience={userExperience}
                header={row.updated_at === 'N/A'}
                noInstitutionalLogo
                aria-label={`Download ${description || row.title} document`}
                assetURL={assetUrl}
              />
              {isInstitutional && (
                <StyledLighthouse
                  src={LighthouseIcon}
                  alt="Institutional investors only: Login required"
                />
              )}
              {formattedDate && (
                <DateTypography variant="body2">
                  As of {formattedDate}
                </DateTypography>
              )}
            </DownloadContainer>
          )
        },
      },
    ],
    []
  )

  const tableData = useMemo(() => {
    const rows = []
    if (fundDocuments.length > 0) {
      rows.push({
        description: 'Fund Documents',
        innerTitle: 'Fund Documents',
        updated_at: 'N/A',
        grayBackground: true,
      })
      fundDocuments.forEach((doc) => rows.push(doc))
    }
    if (regulatory.length > 0) {
      rows.push({
        description: 'Regulatory',
        innerTitle: 'Regulatory',
        updated_at: 'N/A',
        grayBackground: true,
      })
      regulatory.forEach((doc) => rows.push(doc))
    }
    if (additionalDocuments.length > 0) {
      rows.push({
        description: 'Additional Documents',
        innerTitle: 'Additional Documents',
        updated_at: 'N/A',
        grayBackground: true,
      })
      additionalDocuments.forEach((doc) => rows.push(doc))
    }
    return rows
  }, [fundDocuments, regulatory, additionalDocuments])

  return (
    <Container>
      <ScrollingTable data={tableData} columns={columns} />
    </Container>
  )
}

FormsResources.propTypes = {
  data: PropTypes.shape({
    seismic_quarterly_report: PropTypes.arrayOf(
      PropTypes.shape({
        document: PropTypes.object,
        date: PropTypes.string,
      })
    ),
    seismic_portfolio_holdings: PropTypes.arrayOf(
      PropTypes.shape({
        document: PropTypes.object,
        date: PropTypes.string,
      })
    ),
    index_methodology: PropTypes.object,
    index_methodology_as_of_date: PropTypes.string,
    index_fact_sheet: PropTypes.object,
    summary_prospectus: PropTypes.object,
    prospectus: PropTypes.object,
    semi_annual_tailored_shareholder_report: PropTypes.object,
    tailored_shareholder_report: PropTypes.object,
    supplementary_financial_information: PropTypes.object,
    annual_report: PropTypes.object,
    semi_annual_report: PropTypes.object,
    sai: PropTypes.object,
    fiscal_quarter_end_q3: PropTypes.object,
    fiscal_quarter_end_q1: PropTypes.object,
    form_n_csr: PropTypes.object,
    seismic_additional_documents: PropTypes.arrayOf(
      PropTypes.shape({
        seismic_document: PropTypes.arrayOf(PropTypes.object),
        user_experience: PropTypes.object,
      })
    ),
    additional_documents: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
}

export default FormsResources
